import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { DurationRange } from '@app-core/models/core.model';
import { AssetAutoCompleteResponseObject } from '@app-core/models/core.model';
import { DateService } from '@app-core/services/date/date.service';
import { getActiveDriverFilter } from '@app-trips/reducers';
import { State } from '@app-trips/reducers/activeDrivers-filter.reducer';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-active-driver-duration',
  templateUrl: './active-driver-duration.component.html',
  styleUrls: ['./active-driver-duration.component.scss'],
})
export class ActiveDriverDurationComponent implements OnInit, OnDestroy {
  @Input()
  set driverList(drivers) {
    this.translate.stream('All Drivers').subscribe((text: string) => {
      this.ALL_DRIVERS_ID = text;
      this._driverList = [{ driverId: this.ALL_DRIVERS_ID }, ...drivers];
    });
  }
  @Input()
  public currentTabIndex = 0;
  @Input()
  public entityType;

  @Output()
  public tripFilterChange = new EventEmitter<any>();

  public _driverList = [];
  private ALL_DRIVERS_ID = 'All Drivers';
  private ALL_ASSET_ID = null;

  public durationRange: DurationRange;
  public form = null;
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  public filterTypeList = [
    {
      label: 'Driver',
      value: 'driverFilter',
    },
    {
      label: 'Asset',
      value: 'assetFilter',
    },
  ];
  public customOptions = {
    showLargeInput: false,
    showAdditionalDisplayProp: true,
    additionalDisplayPropKey: 'driverName',
  };
  public tagIds = [];
  private ngUnsubscribeOnChanges: Subject<void> = new Subject<void>();
  public tripTags: any[];
  constructor(private store: Store<State>, private fb: FormBuilder, private dateService: DateService, public translate: TranslateService) {}

  public ngOnInit() {
    const d = new Date();
    const defaultStartDate = new Date(d.setDate(d.getDate() - 7));

    this.form = this.fb.group({
      days: [7, Validators.required],
      startDate: [defaultStartDate, [Validators.required]],
      endDate: [new Date(), [Validators.required]],
      filterType: ['driverFilter', Validators.required],
      driverId: [this.ALL_DRIVERS_ID],
      asset: [],
      tagIds: [''],
    });

    this.store
      .select(getActiveDriverFilter)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((tripsFilter) => {
        const { days, displayStartDate: startDate, displayEndDate: endDate } = tripsFilter;
        this.durationRange = { days, startDate, endDate };
        this.form.patchValue({
          ...this.durationRange,
          filterType: tripsFilter.filterType,
          driverId: tripsFilter.driverId || this.ALL_DRIVERS_ID,
          asset: tripsFilter.asset || this.ALL_ASSET_ID,
          tagIds: tripsFilter.tagIds,
        });
      });

    this.store
      .select(getActiveDriverFilter)
      .pipe(takeUntil(this.ngUnsubscribeOnChanges))
      .subscribe((tagFilter) => {
        const { tagIds } = tagFilter;
        this.tagIds = tagIds;
      });
    this.tripTags = this.tagIds.length ? this.tagIds.map((tagid) => ({ tagId: tagid })) : [];
  }

  public driverChanged(driverId = '') {
    this.form.patchValue({ driverId });
  }

  public assetChanged(asset: AssetAutoCompleteResponseObject) {
    this.form.patchValue({ asset });
  }

  public durationChange(durationRange: DurationRange) {
    const { days, startDate, endDate } = durationRange;
    if (days && startDate && endDate) {
      this.form?.patchValue({
        days: days,
        startDate: startDate,
        endDate: endDate,
      });
    }
  }

  /**
   * Function called on click of search button
   *
   * Emit search event.
   */
  public onSearch() {
    const { days, startDate, endDate, driverId, asset, filterType } = this.form.value;

    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const seconds = now.getSeconds();
    const milliSeconds = now.getMilliseconds();

    // Setting current Hours, minutes and seconds since datepicker only sets date part.
    const from = this.dateService.toDaysStartISO(new Date(startDate.setHours(hours, minutes, seconds, milliSeconds)));
    const to = this.dateService.toDaysEndISOPlusOne(new Date(endDate.setHours(hours, minutes, seconds, milliSeconds)));

    let selectedDriverId = driverId !== this.ALL_DRIVERS_ID ? driverId : '';
    let selectedAsset = asset || null;
    const index = this._driverList.findIndex((d) => d.driverId === selectedDriverId);
    let selectedDriverName = index > -1 ? this._driverList[index].driverName : '';

    if (filterType === 'assetFilter') {
      selectedDriverId = undefined;
      selectedDriverName = undefined;
    } else {
      selectedAsset = null;
    }

    this.tripFilterChange.emit({
      days: days,
      displayStartDate: startDate,
      displayEndDate: endDate,
      paramStartDate: from,
      paramEndDate: to,
      filterType,
      driverId: selectedDriverId,
      driverName: selectedDriverName,
      asset: selectedAsset,
      tagIds: this.tagIds,
    });
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.ngUnsubscribeOnChanges.next();
    this.ngUnsubscribeOnChanges.complete();
  }

  public selectedTags(tags: any) {
    this.tagIds = tags;
    this.form.get('tagIds').patchValue(tags);
  }
}
